import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import ExperienceShowcase from '@/components/invite/ExperienceShowcase';
import {
  TopConversionBar, QuickTrialCTA, EmotionalHookSection, ViralHookSection, StickyMobileTrialCTA, TrustMiniStrip, } from '@/components/invite/ConversionEnhancements';
import {
  saveInviteContext, clearInviteContext, type InviteAcceptanceMode, type PendingInviteType, } from '@/lib/inviteContext';
import { useMembership } from '@/contexts/MembershipContext';
import {
  decideMembershipInviteClaim, isInviteLifecycleSchemaError, } from '@/governor/membership/MembershipInviteClaimRuntime';
import {
  Heart, Loader2, Sparkles, ShieldCheck, Users, GitBranch, ArrowRight } from 'lucide-react';

/**
 * Public landing page reached by every viral invite link of the form:
 *   https://www.kinscircleapp.com/invite/{inviter_user_id}
 *
 * UPGRADED (conversion + urgency pass):
 *   - TOP conversion bar with invite context ("{Name} invited you…")
 *   - Above-the-fold "Start your family in 60 seconds" trial CTA card
 *     pinned directly under the hero so users see the offer before
 *     scrolling.
 *   - Emotional hook ("Don't lose your family's story")
 *   - Viral hook ("Build something your whole family will use")
 *   - Sticky mobile bottom Start-Free-Trial button.
 *   - Founder's-price urgency line repeated near the CTA.
 *
 * Existing invite-link, kc:pendingInvite handoff, auth and Stripe
 * trial-checkout flows are completely untouched — every new piece is
 * additive and only forwards clicks to the host's existing
 * openMembershipModal / openAuthModal callbacks.
 */
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const TOKEN_RE = /^[a-zA-Z0-9_-]{16,128}$/;

type InviteErrorKind =
  | 'invalid'
  | 'expired'
  | 'used'
  | 'notfound'
  | 'already-joined';

const InviteLanding: React.FC = () => {
  const { inviterId } = useParams<{ inviterId: string }>();
  const navigate = useNavigate();
  const { user, isAuthenticated, loading } = useAuth();
  const {
    openAuthModal,
    openMembershipModal,
  } = useMembership();

  const [inviter, setInviter] = useState<{ id: string; name: string; family_name: string | null } | null>(null);
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [errorKind, setErrorKind] = useState<InviteErrorKind | null>(null);
  const [lookingUp, setLookingUp] = useState(true);
  const [inviteType, setInviteType] =
    useState<PendingInviteType>('FAMILY_PROFILE');
  const [reservationExpiresAt, setReservationExpiresAt] =
    useState<string | null>(null);
  const [acceptanceMode, setAcceptanceMode] =
    useState<InviteAcceptanceMode | null>(null);

  // Pull the per-invite token (?t=<token>) from the URL once. This is
  // optional — legacy links without a token still resolve, they just
  // can't track lifecycle events as precisely.
  const urlToken = useMemo(() => {
    if (typeof window === 'undefined') return null;
    try {
      const t = new URL(window.location.href).searchParams.get('t');
      return t && TOKEN_RE.test(t) ? t : null;
    } catch {
      return null;
    }
  }, []);

  // Look up the inviter by id. EMAIL IS NOT REQUIRED — invite links are
  // designed to work via manual share (copy/paste, WhatsApp, SMS, AirDrop,
  // QR). We resolve the inviter by user id from multiple sources so we
  // never falsely return "account not found" when the user clearly exists:
  //   1. profiles table (preferred — has display name + family name)
  //   2. family_graph_members.owner_user_id (fallback — tree exists but
  //      profile row never got written, e.g. legacy account)
  //   3. invites table by inviter_user_id (last resort)
  //
  // BULLETPROOF EXTENSION: if a token is present we ALSO validate the
  // specific invite row up-front (expired? already-used?) so the user
  // gets a clear, specific error instead of being silently dropped into
  // a generic onboarding flow.
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      // Step 0 — validate the link format itself.
      if (!inviterId) {
        setErrorKind('invalid');
        setLookupError('This invite link is missing the inviter id.');
        setLookingUp(false);
        return;
      }
      if (!UUID_RE.test(inviterId)) {
        setErrorKind('invalid');
        setLookupError('This invite link is malformed. Please ask the sender to re-share it.');
        setLookingUp(false);
        return;
      }
      setLookingUp(true);
      try {
        if (!urlToken) {
          setErrorKind('invalid');
          setLookupError(
            'This invitation is missing its secure token. Ask the sender to create a new invitation.',
          );
          setLookingUp(false);
          return;
        }

        const {
          data: resolution,
          error: resolutionError,
        } = await supabase.rpc(
          'resolve_invite_landing',
          {
            p_inviter_id: inviterId,
            p_token: urlToken,
          },
        );

        if (cancelled) return;

        if (resolutionError) {
          throw resolutionError;
        }

        if (
          !resolution ||
          typeof resolution !== 'object'
        ) {
          throw new Error(
            'Invite resolution returned no result.',
          );
        }

        const resolutionStatus =
          typeof resolution.status === 'string'
            ? resolution.status
            : 'notfound';

        if (resolutionStatus === 'expired') {
          setErrorKind('expired');
          setLookupError(
            resolution.reason ===
              'SPONSORED_RESERVATION_EXPIRED'
              ? 'This sponsored membership seat expired and returned to the sender. Ask them to send a new sponsored invitation.'
              : 'This invite link expired. Ask the sender to send a new invitation.',
          );
          setLookingUp(false);
          return;
        }

        if (resolutionStatus === 'used') {
          setErrorKind('used');
          setLookupError(
            'This invitation has already been used. Ask the sender to create a new invitation for you.',
          );
          setLookingUp(false);
          return;
        }

        if (
          resolutionStatus !== 'ok' ||
          !resolution.inviter_id
        ) {
          setErrorKind(
            resolutionStatus === 'invalid'
              ? 'invalid'
              : 'notfound',
          );
          setLookupError(
            'This invitation could not be verified. Ask the sender to create a new invitation.',
          );
          setLookingUp(false);
          return;
        }

        const resolvedInviteType:
          PendingInviteType =
            resolution.invite_type ===
            'SPONSORED_SEAT'
              ? 'SPONSORED_SEAT'
              : 'FAMILY_PROFILE';

        setInviteType(resolvedInviteType);

        setReservationExpiresAt(
          resolution.seat_reservation_expires_at ??
            resolution.expires_at ??
            null,
        );

        setInviter({
          id: resolution.inviter_id,
          name:
            resolution.inviter_name ||
            'A family member',
          family_name:
            resolution.inviter_family_name ??
            null,
        });

        setLookingUp(false);
        return;
      } catch (e: any) {
        setErrorKind('notfound');
        setLookupError(e?.message || 'Could not load invite.');
      } finally {
        if (!cancelled) setLookingUp(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [inviterId, urlToken, user?.id]);

  // Persist the pending-invite link (with token) to BOTH localStorage AND
  // sessionStorage so the relationship-onboarding modal can pick it up
  // after auth completes — even when the user signs up inside an in-app
  // webview (Messenger / IG / SMS preview) that hands off to the system
  // browser and wipes localStorage.
  useEffect(() => {
    if (inviter) {
      saveInviteContext({
        inviterId: inviter.id,
        inviterName: inviter.name,
        token: urlToken,
        inviteType,
        acceptanceMode,
      });
    }
  }, [
    inviter,
    urlToken,
    inviteType,
    acceptanceMode,
  ]);

  // Logged-in handling:
  //   - If they ARE the inviter, just send them home.
  //   - Otherwise, save invite context (above) and route to '/app?invited=1'.
  //     AppLayout's resolvePendingInvite() picks up the saved context and
  //     pops the RelationshipOnboardingModal which performs the actual
  //     account ↔ family_graph_members binding. No email is involved.
  useEffect(() => {
    if (
      !loading &&
      isAuthenticated &&
      user &&
      inviter &&
      user.id !== inviter.id &&
      acceptanceMode
    ) {
      navigate('/app?invited=1', {
        replace: true,
      });
    }

    if (
      !loading &&
      isAuthenticated &&
      user &&
      inviter &&
      user.id === inviter.id
    ) {
      navigate('/app', { replace: true });
    }
  }, [
    loading,
    isAuthenticated,
    user,
    inviter,
    acceptanceMode,
    navigate,
  ]);


  const inviterFirstName = useMemo(() => inviter?.name.split(' ')[0] ?? 'A family member', [inviter]);

  const persistAcceptance = (
    mode: InviteAcceptanceMode,
  ) => {
    if (!inviter) return;

    setAcceptanceMode(mode);

    saveInviteContext({
      inviterId: inviter.id,
      inviterName: inviter.name,
      token: urlToken,
      inviteType,
      acceptanceMode: mode,
    });
  };

  const continueWithAcceptance = (
    mode: InviteAcceptanceMode,
  ) => {
    persistAcceptance(mode);

    if (isAuthenticated) {
      if (mode === 'SELF_PAID') {
        openMembershipModal();
        return;
      }

      navigate('/app?invited=1');
      return;
    }

    openAuthModal('signup');
  };

  const handleSignInToInvite = () => {
    const mode =
      acceptanceMode ??
      (inviteType === 'SPONSORED_SEAT'
        ? 'SPONSORED_SEAT'
        : 'FAMILY_CONTRIBUTOR');

    persistAcceptance(mode);
    openAuthModal('signin');
  };

  const handleReviewOptions = () => {
    document
      .getElementById('invite-options')
      ?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
  };

  const handleStartTrial = () => {
    if (inviter) {
      continueWithAcceptance('SELF_PAID');
      return;
    }

    openAuthModal('signup');
  };

  const handleJoinFamily = () => {
    handleReviewOptions();
  };

  const handleStartTree = () => {
    // User explicitly chose "Start My Own Tree" — drop any pending invite
    // so they fall into the organic-signup path and get a fresh root.
    clearInviteContext();

    if (isAuthenticated) {
      navigate('/app');
      return;
    }

    openAuthModal('signup');
  };


  // Show top bar only when we've successfully resolved an inviter and the
  // user isn't already signed in (otherwise they're about to redirect).
  const showTopBar = !lookingUp && inviter && !isAuthenticated;

  return (
    <div className="min-h-screen bg-gradient-to-br from-fuchsia-50 via-pink-50 to-amber-50">
      {/* ─────────────── Top conversion bar ─────────────── */}
      {showTopBar && (
        <TopConversionBar
          inviterName={inviter?.name ?? null}
          onCta={handleJoinFamily}
        />
      )}

      <header className="px-4 sm:px-8 py-5 flex items-center justify-between max-w-6xl mx-auto">
        <Link to="/" className="flex items-center gap-2 group">
          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-fuchsia-500 to-pink-500 flex items-center justify-center text-white shadow group-hover:scale-105 transition">
            <img
              src="/brand/kinscircle-tree-icon.png"
              alt="KinsCircle"
              className="w-full h-full rounded-full object-cover"
            />
          </div>
          <div className="font-serif text-xl font-bold text-slate-900">
            Kins<span className="bg-gradient-to-r from-fuchsia-600 to-pink-500 bg-clip-text text-transparent">Circle</span>
          </div>
        </Link>
        {!isAuthenticated && (
          <button
            onClick={handleSignInToInvite}
            className="text-sm font-semibold text-fuchsia-700 hover:text-fuchsia-900 hover:underline underline-offset-4"
          >
            Sign in
          </button>
        )}
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-8 pb-16">
        {/* ─────────────── Loading / error states ─────────────── */}
        {(lookingUp || loading) && (
          <div className="rounded-3xl bg-white/80 border border-fuchsia-100 p-8 text-center max-w-2xl mx-auto">
            <Loader2 className="w-6 h-6 animate-spin mx-auto text-fuchsia-600 mb-2" />
            <div className="text-sm text-slate-600">Looking up your invite…</div>
          </div>
        )}

        {!lookingUp && lookupError && (
          <div className="rounded-3xl bg-white border border-rose-200 p-8 text-center max-w-2xl mx-auto">
            <div className="font-serif text-2xl font-bold text-slate-900 mb-2">
              {errorKind === 'invalid' && 'Invite link looks broken'}
              {errorKind === 'expired' && 'This invite has expired'}
              {errorKind === 'used' && 'This invite has already been used'}
              {(!errorKind || errorKind === 'notfound') && 'Invite not found'}
            </div>
            <p className="text-sm text-slate-600 mb-5">{lookupError}</p>
            <p className="text-xs text-slate-500 mb-5">
              Ask the family member who sent it to copy the link from their{' '}
              <span className="font-semibold">Invite</span> screen and share it
              again. Email isn't required — they can paste the link in any
              message app.
            </p>
            <Button onClick={() => navigate('/')} className="bg-fuchsia-600 hover:bg-fuchsia-700 text-white">
              Go to KinsCircle
            </Button>
          </div>
        )}


        {/* ─────────────── Invite-context banner (only when an inviter is resolved) ─────────────── */}
        {!lookingUp && inviter && !isAuthenticated && (
          <div className="rounded-3xl overflow-hidden bg-white shadow-xl border border-fuchsia-100 mb-8 mt-2">
            <div className="bg-gradient-to-br from-fuchsia-600 via-pink-500 to-amber-400 text-white p-7 sm:p-9 text-center">
              <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/20 backdrop-blur text-[11px] font-bold uppercase tracking-widest mb-4">
                <Sparkles className="w-3.5 h-3.5" /> You've been invited
              </div>
              <h1 className="font-serif text-3xl sm:text-4xl font-bold leading-tight">
                You've been invited to join
                <br />
                <span className="text-amber-100">{inviterFirstName}'s family</span>
                <br className="hidden sm:block" /> on KinsCircle
              </h1>
              <p className="mt-3 opacity-95 text-sm sm:text-base max-w-md mx-auto">
                {inviter.name} invited you to help build your shared family legacy — photos, memories, and private family conversations,
                memories, the family tree, all in one private place.
              </p>
            </div>
            <div
              id="invite-options"
              className="p-6 sm:p-8 space-y-5"
            >
              <div className="text-center">
                <div className="text-xs font-bold uppercase tracking-widest text-fuchsia-700">
                  Choose how you want to join
                </div>
                <p className="mt-2 text-sm text-slate-600">
                  Opening this link does not claim a seat.
                  You choose what happens next.
                </p>
              </div>

              {inviteType === 'SPONSORED_SEAT' && (
                <div className="rounded-2xl border-2 border-violet-200 bg-violet-50/70 p-5">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="font-serif text-xl font-bold text-violet-950">
                        Sponsored KinsCircle Membership
                      </div>
                      <p className="mt-1 text-sm text-violet-900/75">
                        {inviter.name} reserved a paid
                        membership seat for you.
                      </p>
                    </div>
                    <span className="rounded-full bg-violet-700 px-2.5 py-1 text-[10px] font-bold uppercase text-white">
                      Sponsored
                    </span>
                  </div>

                  <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-violet-950">
                    <Benefit text="Claim and manage your profile" />
                    <Benefit text="Join and build the family tree" />
                    <Benefit text="Family Message Board" />
                    <Benefit text="Private messaging" />
                    <Benefit text="Voice and video calls" />
                    <Benefit text="Expanded photo and video access" />
                  </div>

                  {reservationExpiresAt && (
                    <p className="mt-3 text-xs font-medium text-violet-800">
                      Seat reserved until{' '}
                      {new Date(
                        reservationExpiresAt,
                      ).toLocaleDateString()}.
                    </p>
                  )}

                  <Button
                    onClick={() =>
                      continueWithAcceptance(
                        'SPONSORED_SEAT',
                      )
                    }
                    className="mt-4 w-full h-12 bg-violet-700 hover:bg-violet-800 text-white"
                  >
                    Accept My Invitation
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              )}

              <div className="rounded-2xl border border-emerald-200 bg-emerald-50/70 p-5">
                <div className="font-serif text-xl font-bold text-emerald-950">
                  Join as a Family Contributor
                </div>
                <p className="mt-1 text-sm text-emerald-900/75">
                  Join the family without using a sponsored
                  membership seat.
                </p>

                <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-emerald-950">
                  <Benefit text="Claim and edit your own profile" />
                  <Benefit text="Join and add to the family tree" />
                  <Benefit text="Read and participate on the Message Board" />
                  <Benefit text="Upload five total photos" />
                  <Limit text="No private messaging or calls" />
                  <Limit text="No video or premium media" />
                </div>

                <Button
                  onClick={() =>
                    continueWithAcceptance(
                      'FAMILY_CONTRIBUTOR',
                    )
                  }
                  variant="outline"
                  className="mt-4 w-full h-12 border-emerald-300 text-emerald-900 hover:bg-emerald-100"
                >
                  Join as Family Contributor
                </Button>
              </div>

              <div className="rounded-2xl border border-amber-200 bg-amber-50/70 p-5">
                <div className="font-serif text-lg font-bold text-amber-950">
                  Prefer your own membership?
                </div>
                <p className="mt-1 text-sm text-amber-900/75">
                  Purchase your own plan, become a sponsor,
                  and add KinsSpace whenever you are ready.
                  Your family connection and profile stay with you.
                </p>

                <Button
                  onClick={() =>
                    continueWithAcceptance(
                      'SELF_PAID',
                    )
                  }
                  variant="outline"
                  className="mt-4 w-full h-11 border-amber-300 text-amber-950 hover:bg-amber-100"
                >
                  Choose My Membership
                </Button>
              </div>

              <button
                onClick={handleSignInToInvite}
                className="w-full text-xs text-fuchsia-700 hover:text-fuchsia-900 underline-offset-2 hover:underline"
              >
                Already have an account? Sign in and continue
              </button>

              <p className="text-center text-xs leading-relaxed text-slate-500">
                Your family invited you because they want
                you to help preserve your family&apos;s
                history, memories, and future generations
                on KinsCircle.
              </p>
            </div>
          </div>
        )}

        {/* ─────────────── Hero ─────────────── */}
        {!lookingUp && !lookupError && (
          <>
            <section className="text-center mb-7">
              <h2 className="font-serif text-4xl sm:text-5xl md:text-6xl font-bold text-slate-900 leading-[1.05]">
                Build Your Family's
                <br />
                <span className="bg-gradient-to-r from-fuchsia-600 via-pink-500 to-amber-500 bg-clip-text text-transparent">
                  Living Legacy
                </span>
              </h2>
              <p className="mt-5 text-base sm:text-lg text-slate-600 max-w-xl mx-auto">
                Connect generations, share memories, and grow your family story — all in one place.
              </p>

              <div className="mt-7 flex flex-col sm:flex-row gap-3 justify-center max-w-xl mx-auto">
                <Button
                  onClick={inviter ? handleJoinFamily : handleStartTree}
                  className="h-14 text-base bg-gradient-to-r from-fuchsia-600 via-pink-500 to-amber-400 hover:opacity-95 text-white font-semibold gap-2 shadow-xl rounded-2xl px-8"
                >
                  <Sparkles className="w-5 h-5" />
                  {inviter ? 'Join My Family' : 'Start My Family Tree'}
                  <ArrowRight className="w-5 h-5 ml-1" />
                </Button>
                {inviter ? (
                  <Button
                    onClick={handleStartTree}
                    variant="outline"
                    className="h-14 text-base border-2 border-fuchsia-200 hover:border-fuchsia-400 hover:bg-fuchsia-50 text-fuchsia-800 font-semibold rounded-2xl px-8"
                  >
                    <GitBranch className="w-5 h-5 mr-2" />
                    Start My Own Tree
                  </Button>
                ) : (
                  <Button
                    onClick={() => navigate('/login')}
                    variant="outline"
                    className="h-14 text-base border-2 border-fuchsia-200 hover:border-fuchsia-400 hover:bg-fuchsia-50 text-fuchsia-800 font-semibold rounded-2xl px-8"
                  >
                    <Users className="w-5 h-5 mr-2" />
                    Sign In
                  </Button>
                )}
              </div>

              <div className="mt-5">
                <TrustMiniStrip />
              </div>
            </section>

            {/* ─────────────── Quick trial CTA (above the fold) ─────────────── */}
            <div className="mb-10">
              <QuickTrialCTA
                onCta={handleStartTrial}
                label={inviter ? 'Start Free Trial & Join' : 'Start Free Trial'}
              />
            </div>

            {/* ─────────────── Emotional hook ─────────────── */}
            <div className="mb-10">
              <EmotionalHookSection />
            </div>

            {/* ─────────────── Viral hook ─────────────── */}
            <div className="mb-10">
              <ViralHookSection />
            </div>

            {/* ─────────────── Full feature/conversion showcase ─────────────── */}
            <ExperienceShowcase
              onPrimaryCta={handleStartTrial}
              primaryLabel={inviter ? 'Start Free Trial & Join' : 'Start Free Trial'}
            />

            <p className="mt-10 text-center text-[11px] text-slate-500">
              By continuing you agree to our{' '}
              <Link to="/terms" className="underline hover:text-slate-700">Terms</Link> and{' '}
              <Link to="/privacy" className="underline hover:text-slate-700">Privacy</Link>.
            </p>
          </>
        )}
      </main>

      {/* ─────────────── Sticky mobile bottom CTA ─────────────── */}
      {!lookingUp && !lookupError && (
        <StickyMobileTrialCTA
          onCta={handleStartTrial}
          label={inviter ? 'Start Free Trial & Join' : 'Start Free Trial'}
        />
      )}
    </div>
  );
};

const Benefit: React.FC<{
  text: string;
}> = ({ text }) => (
  <div className="flex items-start gap-2">
    <span
      aria-hidden="true"
      className="mt-0.5 font-bold text-emerald-600"
    >
      ✓
    </span>
    <span>{text}</span>
  </div>
);

const Limit: React.FC<{
  text: string;
}> = ({ text }) => (
  <div className="flex items-start gap-2 text-slate-600">
    <span
      aria-hidden="true"
      className="mt-0.5 font-bold text-slate-400"
    >
      —
    </span>
    <span>{text}</span>
  </div>
);

const Feature: React.FC<{ icon: React.ReactNode; title: string; body: string }> = ({ icon, title, body }) => (
  <div className="rounded-2xl bg-fuchsia-50/70 border border-fuchsia-100 p-3">
    <div className="inline-flex w-9 h-9 rounded-full bg-white items-center justify-center shadow-sm border border-fuchsia-100 mb-2">
      {icon}
    </div>
    <div className="font-bold text-sm text-slate-900">{title}</div>
    <div className="text-[11px] text-slate-600">{body}</div>
  </div>
);

export default InviteLanding;
